/* eslint-disable no-restricted-imports */
import React, {
    ForwardedRef,
    ReactElement,
    cloneElement,
    forwardRef,
    useImperativeHandle,
    useLayoutEffect,
    useRef,
    useState,
} from 'react';
import { createForm, type FormApi, type SubmissionErrors } from 'final-form';
import { trim } from 'lodash';
import Dropzone from 'react-dropzone';
import { Field, Form } from 'react-final-form';
import styled from 'styled-components';
import { ProductItemModel } from '../../../data-layer/posts/types';
import { fromPostPersistenceMediaUI } from '../../../mappers/posts/toPostMediaUI';
import { canSaveOrUpdatePost, canSavePostToDraft } from '../../../utils/accessService';
import { useIsAdvertisingCatalogEnabled } from '../../../utils/useIsAdvertisingCatalogEnabled';
import { useTranslation } from '../../../utils/useTranslation';
import { composeValidators } from '../../../utils/validators/compose';
import { makeClickOutLinkValidator } from '../../../utils/validators/makeClickOutLinkValidator';
import { makeEmptyValidator } from '../../../utils/validators/makeEmptyValidator';
import { makeMaxLengthValidator } from '../../../utils/validators/makeMaxLengthValidator';
import { makeRequiredValidator } from '../../../utils/validators/makeRequiredValidator';
import { makeUrlValidator } from '../../../utils/validators/makeUrlValidator';
import { useDragAndDrop } from '../../../utils/validators/useDragAndDrop';
import { AdvertisingButton } from '../../advertising/AdvertisingButton/AdvertisingButton';
import { Carousel } from '../../carousel/Carousel';
import { CircularProgress } from '../../feedback/CircularProgress';
import { WithHint } from '../../feedback/Tooltip';
import { IconAdd } from '../../icon/IconAdd';
import { IconClose } from '../../icon/IconClose';
import { Button } from '../../input/Button';
import { FormControl } from '../../input/FormControl';
import { IconButton } from '../../input/IconButton';
import { Input } from '../../input/Input';
import { TextArea } from '../../input/TextArea';
import { Upload } from '../../input/Upload';
import { Box } from '../../layout/Box';
import { Flex } from '../../layout/Flex';
import { Stack } from '../../layout/Stack';
import { Image } from '../../media/Image';
import { MediaCover } from '../../media/MediaCover';
import { VideoComponent } from '../../media/VideoComponent';
import { Typography } from '../../typography/Typography';
import { CpaTrialDisclaimer } from '../../users/CpaTrialDisclaimer/CpaTrialDisclaimer';
import { useCpaTrial } from '../../users/CpaTrialDisclaimer/hooks/useCpaTrial';
import { UserUI } from '../../users/types';
import { MediaPreview } from '../MediaPreview';
import { MAX_DESCRIPTION_LENGTH, PostStatus } from '../constants';
import { ReactComponent as MediaSvg } from '../resources/media.svg';
import { type PostPersistenceMediaUI, type PostPersistenceUI } from '../types';
import { AILabelFiled } from './parts/AILabelFiled/AILabelFiled';

const validateText = composeValidators(
    makeRequiredValidator(),
    makeEmptyValidator(),
    makeMaxLengthValidator(MAX_DESCRIPTION_LENGTH, 'Описание не должно превышать 2200 символов'),
);

const validateUrl = composeValidators(
    makeUrlValidator(),
    makeClickOutLinkValidator('Из поста вы можете вести на ваш сайт или маркетплейс'),
);

const validateUrlWithProduct = composeValidators(
    makeUrlValidator(),
    makeClickOutLinkValidator('Вы можете добавить товар с вашего сайта или маркетплейса', false),
);

const validateMedia = (media: any[] = []) =>
    media.length === 0 ? 'Необходимо добавить хотя бы одно фото или видео' : undefined;

const FormSection = styled(Flex)
    .withConfig({ displayName: 'FormSection' })
    .attrs({
        p: 1,
        direction: 'column',
        alignItems: 'stretch',
        overflow: 'hidden',
        borderRadius: { sm: 1.5 },
        background: 'var(--background-primary)',
    })``;

const FileInput = React.forwardRef<HTMLInputElement, React.InputHTMLAttributes<HTMLInputElement>>(
    (props, ref) => (
        <input
            ref={ref}
            type="file"
            style={{
                position: 'absolute',
                opacity: 0,
                width: '100%',
                height: '100%',
                outline: 'none',
                WebkitTapHighlightColor: 'transparent',
            }}
            {...props}
        />
    ),
);

interface MediaEditorProps {
    readOnly: boolean;
    value?: PostPersistenceMediaUI[];
    onChange: (value: PostPersistenceMediaUI[]) => void;
    onUploadFile: (
        file: File,
        onUpdateProgress?: (progress: number) => void,
    ) => Promise<PostPersistenceMediaUI | undefined>;
}

const ACCEPT_MAPPINGS = {
    'image/*': [],
    'video/*': [],
};

const MediaEditor = (props: MediaEditorProps) => {
    const { readOnly, ...other } = props;
    const [activeIndex, setActiveIndex] = useState<number>(0);
    const t = useTranslation();

    const onContentRulesClick = (e: React.MouseEvent<HTMLElement>) => {
        e.stopPropagation();
    };

    return (
        <Upload {...other}>
            {({ isUploading, value = [], onUploadFiles }) => (
                <Box position="relative" overflow="hidden" height="100%">
                    {value.length === 0 ? (
                        <Dropzone
                            multiple={false}
                            disabled={isUploading}
                            accept={ACCEPT_MAPPINGS}
                            onDropAccepted={onUploadFiles}
                        >
                            {({ getRootProps, getInputProps }) => (
                                <MediaCover
                                    ratio={1}
                                    borderRadius={1.5}
                                    backgroundColor="transparent"
                                    border="2px dashed var(--divider)"
                                    {...getRootProps()}
                                >
                                    <Flex
                                        position="absolute"
                                        alignItems="center"
                                        justifyContent="center"
                                        cursor="pointer"
                                    >
                                        <Box maxWidth="260px" textAlign="center" mb={2}>
                                            <MediaSvg
                                                fill="var(--background-secondary)"
                                                height="150"
                                            />
                                            <Typography
                                                variant="body"
                                                color="primary"
                                                weight="medium"
                                                mb={0.5}
                                            >
                                                {t(
                                                    'Выберите фото или видео, или перетащите его в эту область',
                                                )}
                                            </Typography>
                                            <Typography variant="caption" color="secondary">
                                                {t('Видео не больше 200 МБ и не длиннее 3 минут')}
                                            </Typography>
                                        </Box>
                                        {isUploading ? (
                                            <CircularProgress size={32} />
                                        ) : (
                                            <Button type="button">{t('Выбрать файл')}</Button>
                                        )}
                                        <WithHint
                                            mt={1}
                                            stopPropagation
                                            message={
                                                'Соотношение сторон любых материалов поддерживается от 9/16 до 16/9.\n\nВес файла не может превышать 200 мб и длительности 3 минут.'
                                            }
                                        >
                                            <Typography
                                                as="a"
                                                href="https://support-rythm.viewer.diplodoc.yandex.ru/ru/buisness/moderation?revision=6f3e521ea558214845ba7371cc7670e5b2a91489#trebovaniya-k-izobrazheniyam-i-video"
                                                target="_blank"
                                                variant="caption"
                                                color="link"
                                                onClick={onContentRulesClick}
                                            >
                                                {t('Подробнее про контент')}
                                            </Typography>
                                        </WithHint>
                                        <FileInput {...getInputProps()} />
                                    </Flex>
                                </MediaCover>
                            )}
                        </Dropzone>
                    ) : (
                        <Flex
                            p={1}
                            spacing={1}
                            height="100%"
                            background="var(--background-primary)"
                        >
                            <Box position="relative" width="100%">
                                <MediaEditorCarousel
                                    items={value}
                                    activeIndex={activeIndex}
                                    onSlideChange={setActiveIndex}
                                />

                                {value.length > 1 && (
                                    <Stack
                                        count={value.length}
                                        direction="row"
                                        position="absolute"
                                        p={1}
                                        left="50%"
                                        bottom={1}
                                        spacing={1}
                                        borderRadius={1.5}
                                        transform="translate(-50%, 0)"
                                        background="var(--control-secondary-background)"
                                        backdropFilter="blur(20px)"
                                        renderItem={(index) => (
                                            <Box
                                                key={index}
                                                width={6}
                                                height={6}
                                                borderRadius="50%"
                                                background="var(--control-secondary-color)"
                                                opacity={activeIndex === index ? 1 : 0.5}
                                            />
                                        )}
                                    />
                                )}
                            </Box>

                            <MediaEditorPreview
                                readOnly={readOnly}
                                items={value}
                                isUploading={isUploading}
                                activeIndex={activeIndex}
                                onSelect={setActiveIndex}
                                onChange={props.onChange}
                                onUploadFiles={onUploadFiles}
                            />
                        </Flex>
                    )}
                </Box>
            )}
        </Upload>
    );
};

interface MediaEditorCarouselProps {
    items: PostPersistenceMediaUI[];
    activeIndex: number;
    onSlideChange: (index: number) => void;
}

const MediaEditorCarousel = (props: MediaEditorCarouselProps) => {
    const { items, activeIndex, onSlideChange } = props;
    const [firstMedia] = items;
    const ratio = (firstMedia.width || 1) / (firstMedia.height || 1);

    return (
        <Carousel
            data-carousel
            count={items.length}
            width="100%"
            borderRadius={1}
            overflow="hidden"
            activeIndex={activeIndex}
            onSlideChange={onSlideChange}
            renderItem={(index) => {
                const media = items[index];

                return (
                    <Box
                        key={index}
                        height="100%"
                        position="relative"
                        background="var(--background-secondary)"
                    >
                        <Flex alignItems="center" justifyContent="center">
                            <MediaCover ratio={ratio} width="100%">
                                {media.type === 'image' && <Image src={media.url} />}
                                {media.type === 'video' && <VideoComponent src={media.url} />}
                            </MediaCover>
                        </Flex>
                    </Box>
                );
            }}
        />
    );
};

interface MediaEditorPreviewProps {
    readOnly: boolean;
    size?: number;
    items: PostPersistenceMediaUI[];
    isUploading: boolean;
    activeIndex: number;
    onSelect: (index: number) => void;
    onChange: (value: PostPersistenceMediaUI[]) => void;
    onUploadFiles: (files: File[]) => void;
}

const MediaEditorPreview = (props: MediaEditorPreviewProps) => {
    const {
        size = 80,
        readOnly,
        activeIndex,
        items,
        isUploading,
        onSelect,
        onChange,
        onUploadFiles,
    } = props;
    const containerRef = useRef<HTMLDivElement | null>(null);
    const controllerDnD = useDragAndDrop({
        onDrop: (fromIndex, toIndex) => {
            const orderedItems = [...items];

            [orderedItems[fromIndex], orderedItems[toIndex]] = [
                orderedItems[toIndex],
                orderedItems[fromIndex],
            ];

            onChange(orderedItems);
            onSelect(toIndex);
        },
    });

    useLayoutEffect(() => {
        if (containerRef.current) {
            const element = containerRef.current.children[activeIndex] as HTMLElement | undefined;
            const left = Math.max(
                (element?.offsetLeft ?? 0) - containerRef.current.offsetWidth / 2,
                0,
            );

            if (containerRef.current?.scrollLeft !== left) {
                containerRef.current.scrollTo({ left });
            }
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [activeIndex, containerRef.current]);

    const itemsLength = items.length;

    return (
        <Flex
            ref={containerRef}
            disableScrollbar
            direction="row"
            maxWidth="100%"
            overflowY="auto"
            spacing={1}
        >
            {items.map((item, index) => {
                const mediaUI = fromPostPersistenceMediaUI(item);

                if (!mediaUI) {
                    return null;
                }

                return (
                    <Box
                        key={item.id}
                        draggable
                        minWidth={size}
                        cursor="pointer"
                        position="relative"
                        overflow="hidden"
                        borderRadius={2}
                        data-position={index}
                        border={
                            activeIndex === index
                                ? '2px solid var(--color-primary)'
                                : '2px solid transparent'
                        }
                        transform="translate3d(0,0, 0)"
                        onDrop={controllerDnD.onDrop}
                        onDragStart={controllerDnD.onDragStart}
                        onDragLeave={controllerDnD.onDragLeave}
                        onDragOver={controllerDnD.onDragOver}
                        opacity={controllerDnD.payload.draggedTo === Number(index) ? 0.125 : 1}
                        onClick={() => onSelect(index)}
                    >
                        <MediaCover ratio={1} width="100%">
                            <MediaPreview media={mediaUI} />
                        </MediaCover>
                        {!readOnly && (
                            <Box position="absolute" top={1} right={1}>
                                <IconButton
                                    size="xs"
                                    shape="rect"
                                    variant="filled"
                                    onClick={(event) => {
                                        onChange(items.filter((i) => i !== item));

                                        event.stopPropagation();
                                    }}
                                >
                                    <IconClose />
                                </IconButton>
                            </Box>
                        )}
                    </Box>
                );
            })}
            {itemsLength < 10 && !readOnly && (
                <Flex
                    position="relative"
                    minWidth={size}
                    height={size}
                    borderRadius={2}
                    alignItems="center"
                    cursor={isUploading ? 'not-allowed' : 'pointer'}
                    justifyContent="center"
                    border="1px solid var(--divider)"
                >
                    {isUploading ? (
                        <CircularProgress size={24} />
                    ) : (
                        <IconAdd fontSize={24} color="var(--color-primary)" />
                    )}
                    <FileInput
                        key={itemsLength}
                        multiple={false}
                        accept="image/*,video/*"
                        onChange={(e) => {
                            const files = Array.from(e.target.files || []);
                            if (files.length > 0) {
                                onUploadFiles(files);
                            }
                        }}
                    />
                </Flex>
            )}
        </Flex>
    );
};

type ProductUrlFieldProps = Pick<
    PostPersistenceFormProps,
    'onFieldBlur' | 'onProductUrlPaste' | 'products'
>;

const ProductUrlField = ({ onProductUrlPaste, onFieldBlur, products }: ProductUrlFieldProps) => {
    const t = useTranslation();

    const message = !products.length ? t('Не удалось найти товар') : undefined;

    const isAdvertisingCatalogEnabled = useIsAdvertisingCatalogEnabled();

    return (
        <>
            <Typography pb={1.5} variant="body" weight="bold">
                {t('Товары в посте')}
            </Typography>
            <Field
                name="productUrl"
                validate={validateUrlWithProduct}
                render={({ input, meta }) => (
                    <FormControl
                        mb={2}
                        label={t('Ссылка на товар')}
                        error={meta.touched ? Boolean(meta.error) : undefined}
                        message={meta.touched ? message ?? meta.error : undefined}
                    >
                        <Input
                            {...input}
                            type="text"
                            autoComplete="off"
                            placeholder="https://"
                            onBlur={(event) => {
                                onFieldBlur('productUrl', event.target.value);
                                input.onBlur(event);
                            }}
                            onChange={(event) => {
                                input.onChange(event);
                                if (trim(event.currentTarget.value)) {
                                    onProductUrlPaste?.(event.currentTarget.value);
                                }
                            }}
                        />
                    </FormControl>
                )}
            />
            {isAdvertisingCatalogEnabled ? <AdvertisingButton mb={1} /> : null}
        </>
    );
};

interface PostPersistenceFormProps {
    postId?: string;
    initialValues?: Partial<PostPersistenceUI>;
    onUploadFile: (
        file: File,
        onUpdateProgress?: (progress: number) => void,
    ) => Promise<PostPersistenceMediaUI | undefined>;
    onSubmit: (values: PostPersistenceUI) => Promise<SubmissionErrors>;
    onRemove: VoidFunction;
    onSaveToDraft: VoidFunction;
    onFieldBlur: (fieldName: string, value: string) => void;
    user?: UserUI;
    productsSlot?: ReactElement;
    productsChanged: boolean;
    onProductUrlPaste?: (url: string) => void;
    submitDisabled?: boolean;
    isSiteUrlEnabled?: boolean;
    products: ProductItemModel[];
    isProductsEnable?: boolean;
}

export const PostPersistenceForm = forwardRef(
    (props: PostPersistenceFormProps, ref: ForwardedRef<FormApi<PostPersistenceUI>>) => {
        const {
            initialValues = {},
            onSubmit,
            onRemove,
            onUploadFile,
            user,
            onSaveToDraft,
            onFieldBlur,
            productsSlot,
            productsChanged,
            onProductUrlPaste,
            submitDisabled,
            isSiteUrlEnabled,
            products,
            isProductsEnable,
        } = props;
        const formRef = useRef(createForm<PostPersistenceUI>({ initialValues, onSubmit }));
        const { status } = initialValues;
        const t = useTranslation();
        const { isNotRegistered } = useCpaTrial();

        useImperativeHandle(ref, () => formRef.current);

        return (
            // @ts-ignore onSubmit передается через form
            <Form
                form={formRef.current}
                onSubmit={onSubmit}
                render={({ form, invalid, submitting, pristine, submitError, handleSubmit }) => (
                    <Flex
                        as="form"
                        width="100%"
                        flexGrow={{ xs: 1, sm: 0 }}
                        spacing={{ xs: 1, md: 2 }}
                        direction={{ xs: 'column', md: 'row' }}
                        alignItems={{ xs: 'stretch', md: 'flex-start' }}
                        onSubmit={handleSubmit}
                    >
                        <FormSection
                            width={{ md: '40%' }}
                            minWidth={{ md: '320px' }}
                            style={{ flex: '0 0 auto' }}
                        >
                            <Field
                                name="media"
                                validate={validateMedia}
                                render={({ input, meta }) => (
                                    <Box style={{ flexShrink: '0' }}>
                                        <MediaEditor
                                            {...input}
                                            readOnly={
                                                status === PostStatus.PUBLISHED ||
                                                status === PostStatus.LIMITED
                                            }
                                            onUploadFile={onUploadFile}
                                        />
                                        {meta.touched && Boolean(meta.error) ? (
                                            <Typography
                                                variant="caption"
                                                color="error"
                                                textAlign="center"
                                                mt={1}
                                            >
                                                {meta.error}
                                            </Typography>
                                        ) : null}
                                    </Box>
                                )}
                            />
                        </FormSection>
                        <FormSection px={2} flexGrow={1}>
                            <Box>
                                <Typography pb={0.5} variant="body" weight="bold">
                                    {t('Описание')}
                                </Typography>
                                <Field
                                    name="text"
                                    validate={validateText}
                                    render={({ input, meta }) => (
                                        <FormControl
                                            mb={2}
                                            error={meta.touched ? Boolean(meta.error) : undefined}
                                            message={meta.touched ? meta.error : undefined}
                                        >
                                            <TextArea
                                                {...input}
                                                rows={2}
                                                maxLength={MAX_DESCRIPTION_LENGTH}
                                                autoComplete="off"
                                                placeholder={t('Добавить описание поста')}
                                                onBlur={(event) => {
                                                    onFieldBlur('text', event.target.value);
                                                    input.onBlur(event);
                                                }}
                                            />
                                        </FormControl>
                                    )}
                                />
                                {isProductsEnable ? (
                                    <>
                                        <ProductUrlField
                                            onProductUrlPaste={onProductUrlPaste}
                                            onFieldBlur={onFieldBlur}
                                            products={products}
                                        />
                                        {productsSlot ? cloneElement(productsSlot) : null}
                                    </>
                                ) : null}
                                {isSiteUrlEnabled ? (
                                    <>
                                        <Typography pb={1.5} variant="body" weight="bold">
                                            {t('Ссылка на сайт')}
                                        </Typography>
                                        <Field
                                            name="site"
                                            validate={validateUrl}
                                            render={({ input, meta }) => (
                                                <FormControl
                                                    mb={2}
                                                    label={t('Ссылка на сайт')}
                                                    error={
                                                        meta.touched
                                                            ? Boolean(meta.error)
                                                            : undefined
                                                    }
                                                    message={meta.touched ? meta.error : undefined}
                                                >
                                                    <Input
                                                        {...input}
                                                        type="text"
                                                        autoComplete="off"
                                                        placeholder="https://"
                                                        onBlur={(event) => {
                                                            onFieldBlur('site', event.target.value);
                                                            input.onBlur(event);
                                                        }}
                                                    />
                                                </FormControl>
                                            )}
                                        />
                                    </>
                                ) : null}
                                <AILabelFiled
                                    onChange={(event) => {
                                        onFieldBlur(
                                            'isAIContent',
                                            event.currentTarget.checked ? 'checked' : '',
                                        );
                                    }}
                                />
                            </Box>
                            <Box mt="auto" py={1}>
                                {submitError ? (
                                    <Typography variant="caption" color="error" mb={1.5}>
                                        {submitError}
                                    </Typography>
                                ) : null}
                                <>
                                    <Flex direction="row" spacing={1}>
                                        {status === PostStatus.MODERATION ||
                                        status === PostStatus.LIMITED ||
                                        status === PostStatus.PUBLISHED ? (
                                            <Button
                                                type="button"
                                                width="100%"
                                                size="l"
                                                variant="outlined"
                                                onClick={onRemove}
                                            >
                                                {t('Удалить')}
                                            </Button>
                                        ) : (
                                            <Button
                                                type="submit"
                                                width="100%"
                                                size="l"
                                                variant="filled"
                                                color="secondary"
                                                disabled={
                                                    (pristine && !productsChanged) ||
                                                    submitting ||
                                                    !canSavePostToDraft(user?.profile?.id, user)
                                                }
                                                onClick={() => {
                                                    form.change('status', PostStatus.DRAFT);
                                                    onSaveToDraft();
                                                }}
                                            >
                                                {t('В черновики')}
                                            </Button>
                                        )}

                                        <Button
                                            type="submit"
                                            size="l"
                                            width="100%"
                                            disabled={
                                                (status !== PostStatus.DRAFT &&
                                                    pristine &&
                                                    !productsChanged) ||
                                                submitting ||
                                                invalid ||
                                                !canSaveOrUpdatePost(user?.profile?.id, user) ||
                                                submitDisabled
                                            }
                                            onClick={() =>
                                                form.change('status', PostStatus.MODERATION)
                                            }
                                        >
                                            {!status || status === PostStatus.DRAFT
                                                ? t('Опубликовать')
                                                : t('Переопубликовать')}
                                        </Button>
                                    </Flex>
                                    {isNotRegistered ? <CpaTrialDisclaimer /> : null}
                                </>
                            </Box>
                        </FormSection>
                    </Flex>
                )}
            />
        );
    },
);
